import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Products } from 'src/app/shared/services/models/Products';
import { PaginatedCamSpecResponse } from '../search-cam-spec/model/paginated-camspec.model';
import { Limit } from './models/Limit';
import { MRT } from './models/MRT';
import { SpecLine, SpecificationType } from './models/SpecLine';
import { UpdateActionDetails, UpdateSpecificationLine } from './models/request/UpdateSpecificationLine';
import { SpecificationLineResponse, SpecificationResponse } from './models/response/response.model';

const AGE_LIMIT = 120;
const OPTIONS_LIMIT = 5;

interface SpecLineOptions {
  id?: string;
  v4SpecificationId?: string;
  label?: string;
  type?: SpecificationType;
  specCode?: string;
  specDescription?: string;
  specType?: string;
  defaultSpec?: boolean;
  michelinSpec?: boolean;
  applicationDefinition?: string;
  axles?: string[];
  sizes?: string[];
  series?: string[];
  makes?: string[];
  treads?: string[];
  ntr?: ActionDetails;
  age?: ActionDetails;
  repair?: ActionDetails;
  limits?: Limit[];
  exceedMichelinSpec?: boolean;
}

interface MRTOptions {
  application?: string;
  totalSection?: number;
  nailRnail?: number;
  totalCombined?: number;
  spot?: number;
  bead?: number;
  liner?: number;
  age?: number;
  ntr?: number;
}

interface ActionDetails {
  limit?: number;
  action?: string;
  disposition?: string;
  disposalCode?: string;
  application?: string;
  axle?: string;
  treads?: string[];
}

interface ActionDetails {
  limit?: number;
  action?: string;
  disposition?: string;
  disposalCode?: string;
  application?: string;
  axle?: string;
  treads?: string[];
}

export const generateSpecLine: (options?: SpecLineOptions) => SpecLine = (options = {}) => {
  return {
    id: options.id ? options.id : 'ce2c5159-7dc5-46cd-8437-63188519d9f6',
    legacyIds: [] as number[],
    v4SpecificationId: options.v4SpecificationId ? options.v4SpecificationId : 'ce2c5159-7dc5-46cd-8437-63188519d9f7',
    label: options.label ? options.label : 'LABEL',
    type: options.type ? options.type : SpecificationType.REPAIR_RETREAD,
    specCode: options.specCode ? options.specCode : '0123',
    specDescription: options.specDescription ? options.specDescription : 'Mock Description',
    specType: options.specType ? options.specType : null,
    defaultSpec: options.defaultSpec ? options.defaultSpec : false,
    michelinSpec: options.michelinSpec ? options.michelinSpec : false,
    exceedMichelinSpec: options.exceedMichelinSpec ? options.exceedMichelinSpec : false,
    applicationDefinition: options.applicationDefinition ? options.applicationDefinition : 'All',
    sizes: options.sizes ? options.sizes : (['All'] as string[]),
    makes: options.makes ? options.makes : (['All'] as string[]),
    axles: options.axles ? options.axles : (['All'] as string[]),
    series: options.series ? options.series : (['All'] as string[]),
    treads: options.treads ? options.treads : (['All'] as string[]),
    ntr: {
      limit: options.ntr?.limit ? options.ntr.limit : 10,
      action: options.ntr?.action ? options.ntr.action : 'REJECT',
      disposition: options.ntr?.disposition ? options.ntr.disposition : 'SCRAP',
      disposalCode: options.ntr?.disposalCode ? options.ntr.disposalCode : 'TRUCK_TIRE',
      application: options.ntr?.application ? options.ntr.application : null,
      axle: options.ntr?.axle ? options.ntr.axle : null,
      treads: options.ntr?.treads ? options.ntr.treads : ([] as string[])
    },
    age: {
      limit: options.age?.limit ? options.age.limit : AGE_LIMIT,
      action: options.age?.action ? options.age.action : 'REJECT',
      disposition: options.age?.disposition ? options.age.disposition : 'SCRAP',
      disposalCode: options.age?.disposalCode ? options.age.disposalCode : 'TRUCK_TIRE',
      application: options.age?.application ? options.age.application : null,
      axle: options.age?.axle ? options.age.axle : null,
      treads: options.age?.treads ? options.age.treads : ([] as string[])
    },
    repair: {
      limit: options.repair?.limit ? options.repair.limit : null,
      action: options.repair?.action ? options.repair.action : 'REJECT',
      disposition: options.repair?.disposition ? options.repair.disposition : 'SCRAP',
      disposalCode: options.repair?.disposalCode ? options.repair.disposalCode : 'TRUCK_TIRE',
      application: options.repair?.application ? options.repair.application : null,
      axle: options.repair?.axle ? options.repair.axle : null,
      treads: options.repair?.treads ? options.repair.treads : ([] as string[])
    },
    limits: options.limits
      ? options.limits
      : [
          {
            sectionNumber: 0,
            totalRepairLimits: {
              totalRepairs: 0,
              nailRepairs: 0,
              rnailCrownRepairs: 0,
              rnailShoulder: {
                repairs: 0,
                shoulder: {
                  count: 0,
                  size: ''
                },
                crown: {
                  count: 0,
                  size: ''
                }
              },
              section: {
                repairs: 0,
                shoulder: {
                  count: 0,
                  size: ''
                },
                crown: {
                  count: 0,
                  size: ''
                },
                sidewall: {
                  count: 0,
                  size: ''
                }
              }
            },
            otherRepairLimits: {
              spot: 0,
              bead: 0,
              liner: 0,
              brake: 0
            }
          }
        ]
  };
};

export const generateUpdateSpecLine: (options?: SpecLineOptions) => UpdateSpecificationLine = (options = {}) => {
  return {
    id: options.id ? options.id : 'ce2c5159-7dc5-46cd-8437-63188519d9f6',
    type: SpecificationType.REPAIR_RETREAD,
    label: 'Deafult RR',
    specCode: '',
    specDescription: '',
    exceedMichelinSpec: true,
    defaultProductSpec: false,
    makes: [],
    series: [],
    sizes: [],
    applicationDefinition: '',
    axles: [],
    treads: [],
    ntr: {
      limit: 0,
      action: '',
      disposition: '',
      disposalCode: '',
      application: null,
      axle: null,
      treads: null
    },
    age: {
      limit: 0,
      action: '',
      disposition: '',
      disposalCode: '',
      application: null,
      axle: null,
      treads: null
    },
    repair: {
      limit: null,
      action: '',
      disposition: '',
      disposalCode: '',
      application: '',
      axle: '',
      treads: []
    },
    limits: [
      {
        sectionNumber: 0,
        totalRepairLimits: {
          totalRepairs: 0,
          nailRepairs: 0,
          rnailCrownRepairs: 0,
          rnailShoulder: {
            repairs: 0,
            shoulder: {
              count: 0,
              size: ''
            },
            crown: {
              count: 0,
              size: ''
            }
          },
          section: {
            repairs: 0,
            shoulder: {
              count: 0,
              size: ''
            },
            crown: {
              count: 0,
              size: ''
            },
            sidewall: {
              count: 0,
              size: ''
            }
          }
        },
        otherRepairLimits: {
          spot: 0,
          bead: 0,
          liner: 0,
          brake: 0
        }
      }
    ]
  };
};

export const generateMRTLine: (options?: MRTOptions) => MRT = () => {
  return {
    application: 'APP',
    totalSection: 999,
    nailRnail: 999,
    totalCombined: 999,
    spot: 999,
    bead: 999,
    liner: 999,
    age: 999,
    ntr: 999
  };
};

export function generateProducts(): Products {
  return {
    applications: ['LINEHAUL', 'CITY'],
    axles: ['ALL DRIVE', 'TRAILER'],
    treads: ['PXDN', 'PXZA'],
    disposalCodes: ['NO CHARGE', 'PASSENGER TIRE'],
    dispositions: [],
    makes: ['make1', 'make2', 'make3'],
    makeCodes: ['code1', 'code2', 'code3'],
    series: ['series1', 'series2', 'series3'],
    sizes: ['size1', 'size2', 'size3'],
    moldContours: ['contour1', 'contour2', 'contour3'],
    treadBrands: ['brand1', 'brand2', 'brand3'],
    allTreads: ['tread1', 'tread2', 'tread3'],
    rollPartNumbers: ['r1', 'r2', 'r3'],
    finishedPartNumbers: ['f1', 'f2', 'f3'],
    widths: ['width1', 'width2', 'width3'],
    casingGrade: ['grade1', 'grade2', 'grade3']
  };
}

export const generateSpecLineResponse: (...options: SpecLineOptions[]) => SpecificationLineResponse = (...options) => {
  return {
    dataList: options ? options.map((option) => generateSpecLine(option)) : [generateSpecLine()],
    data: null,
    error: [] as string[]
  };
};

export const generateSpecificationResponse: () => SpecificationResponse = () => {
  return {
    dataList: [],
    data: {
      id: 'specification-id',
      name: 'Name',
      description: 'Description',
      type: 'Local',
      specificationLines: [],
      createdBy: '',
      createdDate: '',
      modifiedBy: '',
      modifiedDate: ''
    },
    error: [] as string[]
  };
};

export const generateActionDetails: (options?: ActionDetails) => UpdateActionDetails = (options = {}) => {
  return {
    limit: options.limit ? options.limit : OPTIONS_LIMIT,
    action: options.action ? options.action : null,
    disposition: options.disposition ? options.disposition : null,
    disposalCode: options.disposalCode ? options.disposalCode : null,
    application: options.application ? options.application : null,
    axle: options.axle ? options.axle : null,
    treads: options.treads ? options.treads : ([] as string[])
  };
};

export function repairLimitValidator(targetControl: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const currentValue = control.value;
    const controlToCompare = control.parent?.get(targetControl);

    if (controlToCompare && controlToCompare.value < currentValue) {
      return { repairLimitValidator: true };
    }

    return null;
  };
}

export function generateCamSearchResponse(): PaginatedCamSpecResponse {
  return {
    data: {
      camSpecs: [
        {
          id: 'a06de0b5-97d7-39d6-ad61-1703de2f102e',
          name: '01396',
          description: 'A & D TRANSPORT',
          type: 'LOCAL',
          status: 'Active',
          exceeds: 'Yes',
          usedBy: '1',
          createdDate: '2019-12-08T05:57:44',
          createdBy: 'MIGRATION_USER',
          updatedDate: '2019-12-08T05:57:44',
          updatedBy: 'MIGRATION_USER'
        },
        {
          id: 'b25f41d9-fd4e-3e49-b496-7db83d58b150',
          name: '01396',
          description: 'A & D TRANSPORT',
          type: 'LOCAL',
          status: 'Active',
          exceeds: 'Yes',
          usedBy: '9',
          createdDate: '2019-12-08T05:57:44',
          createdBy: 'MIGRATION_USER',
          updatedDate: '2019-12-08T05:57:44',
          updatedBy: 'MIGRATION_USER'
        }
      ],
      pagination: {
        totalRecords: 2,
        currentPage: 1,
        nextPage: null,
        previousPage: null,
        totalPages: 1
      }
    },
    dataList: [],
    error: []
  };
}
